<template>
    <div
        :id="`process-element-${element.id}`"
        :style="containerStyles"
        class="process-element-overlay"
        ref="processElementOverlay"
        v-if="currentEntityType !== EntityType.FLOW"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
        @mousemove="handleMouseMove"
    >
        <!-- Always render the top level div, since it defines the width -->
        <Popup
            v-if="
                !isEditing &&
                currentEntityId &&
                currentEntityType &&
                isHoverPopupEnabled &&
                businessObject.processElementId
            "
            :anchor="processElementOverlayElement!"
        >
            <GenericCard
                v-if="isHoverPopupEnabled"
                :entityId="currentEntityId"
                :entityType="currentEntityType"
                :showPath="false"
                :entityRefs="enrichedEntityRefs"
                :generatesEvidence="taskIndicators.generatesEvidence"
                :evidenceInstructions="taskIndicators.evidenceInstructions"
                :documentCount="taskIndicators.documentCount"
            />
        </Popup>
        <!-- On a canvas that hosts the element drawer, a click opens that panel
             beside the diagram instead of navigating away from it — the same
             trade the domain object canvas makes (EntitiesDiagram ->
             DomainObjectDrawer). The full element page stays one click away,
             from the panel's "View full details". Only elements backed by a
             ProcessElement take this path; pools and call activities have no
             name/description/entity-refs of their own, so they keep their link
             to whatever they point at. -->
        <button
            v-if="shouldOpenDrawer"
            :id="`process-element-link-${element.id}`"
            type="button"
            class="process-element-link drawer-trigger"
            :class="{
                hover: isHovering,
            }"
            :aria-label="elementLabel"
            data-testid="process-element-drawer-trigger"
            @click="openInElementDrawer"
        ></button>
        <RouterLink
            v-else-if="shouldRenderLink"
            :id="`process-element-link-${element.id}`"
            class="process-element-link"
            :class="{
                hover: isHovering,
            }"
            :to="routerLink"
            :aria-label="elementLabel"
        />
        <div
            v-else
            :id="`process-element-link-${element.id}`"
            class="process-element-link disabled"
        ></div>
    </div>
</template>

<script setup lang="ts">
import { type Element as BpmnElement } from "bpmn-js/lib/model/Types";
import { getBusinessObject } from "bpmn-js/lib/util/ModelUtil";
import { computed, ref, toRef, useTemplateRef } from "vue";
import { useRoute } from "vue-router";

import GenericCard from "@/components/EntityInfo/GenericCard.vue";
import Popup from "@/components/Popup.vue";
import { useBpmnManager } from "@/composables/bpmn-manager";
import * as bpmn_helpers from "@/composables/bpmn-manager/helpers";
import { useEditManager } from "@/composables/edit-manager";
import { decodeArtefactPreview } from "@/lib/artefacts/preview";
import { ProcessElementType, ProcessTask } from "@/lib/openapi/gen";
import { withThreadRevision } from "@/lib/thread-revision-link";
import { useBusinessEntitiesManager } from "@/stores/business-entities-store";
import { EntityType } from "@/stores/entity-store";
import { useProcessElementDrawerStore } from "@/stores/process-element-drawer-store";
import {
    ProcessElement,
    useProcessElementsManager,
} from "@/stores/process-elements-store";

const props = withDefaults(
    defineProps<{
        element: BpmnElement;
        bpmnManager: ReturnType<typeof useBpmnManager>;
        isHoverPopupEnabled?: boolean;
        /** Open ProcessElementDrawer on click instead of navigating to the
         *  element's full page. Set by the canvas that hosts the drawer. */
        isElementDrawerEnabled?: boolean;
    }>(),
    {
        isHoverPopupEnabled: true,
        isElementDrawerEnabled: false,
    }
);

const element = toRef(props.element);
const { processElements } = useProcessElementsManager();
const { entities } = useBusinessEntitiesManager();
const { isEditing } = useEditManager();
const isHovering = ref(false);
const route = useRoute();
const processElementOverlayElement = useTemplateRef("processElementOverlay");
const businessObject = computed(() => getBusinessObject(element.value));

const containerStyles = computed(() => {
    // Add padding to the element container to overlay BPMN element border
    return {
        "width": element.value.width + 16 + "px",
        "height": element.value.height + 16 + "px",
    };
});

/**
 * Get the current element from the processElements list.
 * Falls back to lookup by bpmn_id for tasks not yet embedded with a processElementId
 * in the BPMN XML (e.g. immediately after adding a new task and syncing).
 */
const currentElement = computed<ProcessElement | undefined>(() => {
    const bo = businessObject.value;
    if (bo.processElementId) {
        return processElements.value?.find(
            (t) => t.identity === bo.processElementId
        );
    }
    return processElements.value?.find((t) => t.bpmn_id === bo.id);
});

const currentEntityId = computed(() => {
    const bo = businessObject.value;
    return (
        bo.processElementId ||
        bo.processId ||
        bo.calledElementId ||
        currentElement.value?.identity
    );
});

const currentEntityType = computed(() => {
    const bo = businessObject.value;
    if (bo.processElementId || currentElement.value) {
        const type = bpmn_helpers.elementType(element.value);
        return type ? PROCESS_ELEMENT_ENTITY_TYPE_MAP[type] : undefined;
    } else if (bo.processId) {
        return EntityType.PROCESS;
    }
    return bo.calledElementType as EntityType | undefined;
});

const PROCESS_ELEMENT_ENTITY_TYPE_MAP = {
    [ProcessElementType.TASK]: EntityType.TASK,
    [ProcessElementType.EVENT]: EntityType.EVENT,
    [ProcessElementType.GATEWAY]: EntityType.GATEWAY,
    [ProcessElementType.FLOW]: EntityType.FLOW,
};

const enrichedEntityRefs = computed(() => {
    const refs = currentElement.value?.entity_refs ?? [];
    return refs.map((ref) => ({
        ...ref,
        entityName:
            entities.value?.find((e) => e.identity === ref.entity_id)?.name ??
            ref.entity_id.slice(0, 8) + "…",
    }));
});

const taskIndicators = computed(() => {
    const task =
        currentEntityType.value === EntityType.TASK
            ? (currentElement.value as ProcessTask | undefined)
            : undefined;
    return {
        generatesEvidence: task?.generates_evidence ?? false,
        evidenceInstructions: decodeArtefactPreview(
            task?.evidence_instructions?.preview
        ),
        documentCount: task?.document_count ?? 0,
    };
});

const routerLink = computed(() => {
    // Validate params exist before creating route
    if (!route.params.orgName || !route.params.stekName) {
        return {};
    }

    const navigationRoute = bpmn_helpers.getNavigationRoute(
        element.value,
        route.params.orgName as string,
        route.params.stekName as string
    );
    if (navigationRoute) return withThreadRevision(navigationRoute);

    // Fallback: use the processElements store identity for tasks not yet
    // embedded with a processElementId in the BPMN XML.
    const processElementId = currentElement.value?.identity;
    if (processElementId) {
        const elType = bpmn_helpers.elementType(element.value);
        if (elType) {
            return withThreadRevision({
                name: "processElementPage",
                params: {
                    orgName: route.params.orgName,
                    stekName: route.params.stekName,
                    elementId: processElementId,
                    elementType: elType,
                },
            });
        }
    }
    return {};
});

const shouldRenderLink = computed(() => {
    return Boolean(routerLink.value.name && route.params.stekName);
});

const processElementDrawerStore = useProcessElementDrawerStore();

/** The element's ProcessElementType, or undefined for anything not backed by
 *  a ProcessElement (pools, call activities, sub-processes). */
const drawerElementType = computed(() =>
    bpmn_helpers.elementType(element.value)
);

/**
 * The ProcessElement identity the drawer should open, resolved the same way
 * Definition.vue's own `openElementDrawer` resolves it: the BPMN's embedded id
 * first, the store's list only as the fallback for shapes that predate that
 * stamp.
 *
 * Deliberately not `currentElement.identity` — that needs the process-elements
 * list to have landed, while `routerLink` above is satisfied by the embedded
 * id alone. The two disagreeing meant that during the list fetch the
 * `v-else-if` branch won and a click navigated off the page instead of opening
 * the panel, then opened it a moment later for the very same shape.
 */
const drawerProcessElementId = computed(
    () =>
        businessObject.value.processElementId ||
        processElements.value?.find(
            (candidate) => candidate.bpmn_id === businessObject.value.id
        )?.identity
);

const shouldOpenDrawer = computed(
    () =>
        props.isElementDrawerEnabled &&
        !!drawerProcessElementId.value &&
        !!drawerElementType.value
);

/**
 * Accessible name for whichever overlay control this renders — the drawer
 * trigger or the link.
 *
 * An unnamed event or gateway is legal BPMN and common, and `??` does not
 * substitute for the empty string the API returns for one, so the plain name
 * leaves the control with no accessible name at all. Falls back to the
 * element's type, the same way the drawer's own header falls back to
 * "Unnamed".
 */
const elementLabel = computed(() => {
    // BPMN name first, same precedence as openInElementDrawer below: the
    // store's copy only catches up after a full definition sync.
    const name = businessObject.value.name || currentElement.value?.name;
    if (name) return name;
    return drawerElementType.value
        ? `Unnamed ${drawerElementType.value}`
        : "Unnamed element";
});

const openInElementDrawer = () => {
    const processElementId = drawerProcessElementId.value;
    const type = drawerElementType.value;
    if (!processElementId || !type) return;
    processElementDrawerStore.openDrawer({
        processElementId,
        bpmnId: businessObject.value.id,
        // The BPMN's own name leads the process-elements store's by a full
        // definition sync, so it is the one the panel should start from.
        name: businessObject.value.name || currentElement.value?.name || "",
        elementType: type,
    });
};

const handleMouseMove = (event: MouseEvent) => {
    props.bpmnManager.eventBus?.fire("element.mousemove", {
        element: element.value,
        gfx: undefined,
        originalEvent: event,
        type: undefined,
    });
};

const handleMouseEnter = (event: MouseEvent) => {
    isHovering.value = true;
    props.bpmnManager.eventBus?.fire("element.hover", {
        element: element.value,
        gfx: props.bpmnManager.elementRegistry?.getGraphics(element.value),
        originalEvent: event,
        type: undefined,
    });
};

const handleMouseLeave = (event: MouseEvent) => {
    isHovering.value = false;
    props.bpmnManager.eventBus?.fire("element.out", {
        element: element.value,
        gfx: props.bpmnManager.elementRegistry?.getGraphics(element.value),
        originalEvent: event,
        type: undefined,
    });
};
</script>

<style scoped lang="scss">
/* The drawer trigger is a <button> rather than an <a> because it goes nowhere:
   it opens a panel beside the diagram. That buys keyboard activation and the
   right role for free, but a button also arrives with a UA background, border
   and font of its own, none of which the RouterLink variant has — so they are
   stripped back to the shared .process-element-link box. */
.drawer-trigger {
    appearance: none;
    background: none;
    border: 0;
    cursor: pointer;
}

.process-element-link {
    text-decoration: none;
    padding: 8px;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 0.25em;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;

    // Give the text same properties as the BPMN text.
    color: black;
    font-family: Arial, sans-serif;
    line-height: 1.2;
    font-size: 12px;

    &.hover {
        color: white;
    }

    * {
        max-width: 90%;
        text-wrap-mode: balance;
        overflow-wrap: break-word;
    }
}
</style>
